import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, PhoneCall, ScanLine, Sparkles, Gamepad2 } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      {/* Grid background */}
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60 [mask-image:radial-gradient(ellipse_at_center,_black_30%,_transparent_75%)]" />
      <div aria-hidden className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#FFD600]/30 blur-3xl" />

      <div className="container-secqre relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs font-medium text-[#475569]" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5 text-[#0F172A]" />
              Launching in the United States · Beta access open
            </div>

            <h1 className="mt-6 font-heading font-black text-[3rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A0A0A]" data-testid="hero-heading">
              Your number{" "}
              <span className="highlight-yellow">stays yours.</span>
              <br />
              Your car can still <span className="italic font-light">talk.</span>
            </h1>

            <p className="mt-7 text-lg md:text-xl text-[#475569] max-w-xl leading-relaxed" data-testid="hero-subheading">
              Stick the SECQRE QR on your windshield. Anyone who needs to reach you scans it,
              and you get a <span className="text-[#0A0A0A] font-semibold">masked call, SMS or WhatsApp</span> —
              without ever surrendering your real phone number.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-group">
              <Link to="/pricing" className="btn-primary" data-testid="hero-cta-buy">
                Get my SECQRE tag <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/features" className="btn-ghost" data-testid="hero-cta-learn">
                See how it works
              </Link>
              <Link to="/play" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#FFD600] text-[#0A0A0A] px-5 py-3.5 font-semibold transition-all hover:bg-[#F2C200] hover:-translate-y-0.5" data-testid="hero-cta-play">
                <Gamepad2 className="w-4 h-4" /> Play Honk Patrol
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
              <Bullet icon={<ShieldCheck className="w-4 h-4" />} text="Number never exposed" />
              <Bullet icon={<PhoneCall className="w-4 h-4" />} text="90-second masked calls" />
              <Bullet icon={<ScanLine className="w-4 h-4" />} text="Scan from any phone" />
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md">
              <div aria-hidden className="absolute -inset-6 bg-[#FFD600]/25 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl border border-[#E2E8F0] bg-white p-3 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.18)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_tech-secqre/artifacts/3vobya5t_WhatsApp%20Image%202026-05-18%20at%207.57.38%20PM.jpeg"
                  alt="SECQRE windshield QR sticker — Scan the code to contact the vehicle owner"
                  className="w-full rounded-xl"
                  data-testid="hero-sticker-image"
                />
              </div>

              {/* Floating card 1 */}
              <div className="absolute -left-6 -bottom-6 w-56 rounded-xl border border-[#E2E8F0] bg-white p-4 hidden sm:block animate-fade-up" data-testid="hero-float-call">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FFD600] flex items-center justify-center"><PhoneCall className="w-4 h-4 text-[#0A0A0A]" /></div>
                  <div className="flex-1">
                    <div className="text-xs text-[#475569]">Masked call</div>
                    <div className="text-sm font-semibold">+1 (•••) ••• 0421</div>
                  </div>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-soft" />
                </div>
              </div>

              {/* Floating card 2 */}
              <div className="absolute -right-4 top-6 rounded-xl border border-[#E2E8F0] bg-[#0F172A] text-white px-4 py-3 hidden sm:flex items-center gap-2 animate-fade-up" data-testid="hero-float-scans">
                <span className="font-mono text-xs opacity-70">SCANS</span>
                <span className="font-bold text-lg">12,403</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kinetic marquee */}
      <div className="mt-16 md:mt-24 border-y border-[#E2E8F0] bg-white py-5">
        <Marquee gradient={false} speed={36} pauseOnHover>
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-10 text-[#0A0A0A] font-heading font-semibold text-sm md:text-base">
              <Item>Private by Design</Item>
              <Dot />
              <Item>Masked Calls</Item>
              <Dot />
              <Item>Made for US Drivers</Item>
              <Dot />
              <Item>One Sticker, Lifetime Service</Item>
              <Dot />
              <Item>Works on Any Phone</Item>
              <Dot />
              <Item>QR · NFC · SMS · WhatsApp</Item>
              <Dot />
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

const Bullet = ({ icon, text }) => (
  <span className="inline-flex items-center gap-2 text-[#475569]">
    <span className="text-[#0F172A]">{icon}</span>
    {text}
  </span>
);

const Item = ({ children }) => <span className="whitespace-nowrap">{children}</span>;
const Dot = () => <span className="w-1.5 h-1.5 rounded-full bg-[#FFD600]" />;
